import { SelectionModel } from "@angular/cdk/collections";
import { Component, inject, Input } from "@angular/core";
import { Router } from "@angular/router";
import { TableComponent } from "src/app/table.component";
import { Helpers } from "src/helpers/helpers";
import { CareplusPolicy } from "src/models/careplus-policy";
import { CareplusPolicyStatus } from "src/models/careplus.constants";
import { CareplusOrdersService } from "src/services/careplus-orders.service";
import { CareplusRequestsService } from "src/services/careplus-requests.service";
import { ReportService } from "src/services/report.service";
import { MatLegacySnackBar as MatSnackBar } from "@angular/material/legacy-snack-bar";
import { MAT_DATE_FORMATS } from "@angular/material/core";

export const DATE_FORMATS = {
  parse: {
    dateInput: "DD-MM-YYYY",
  },
  display: {
    dateInput: "DD-MM-YYYY",
    monthYearLabel: "MMM YYYY",
    dateA11yLabel: "DD-MM-YYYY",
    monthYearA11yLabel: "MMMM YYYY",
  },
};

@Component({
  selector: "app-careplus-agency-orders",
  templateUrl: "./careplus-agency-orders.component.html",
  styleUrls: ["./careplus-agency-orders.component.css"],
  providers: [{ provide: MAT_DATE_FORMATS, useValue: DATE_FORMATS }],
})
export class CareplusAgencyOrdersComponent extends TableComponent<CareplusPolicy> {
  private _careplusrequestService = inject(CareplusRequestsService);
  pageTitle = "Orders";
  error: any;
  resendingId: number | null = null;
  baseColumns: string[] = [
    "id",
    "createdAt",
    "status",
    "ownerName",
    "email",
    "phoneNumber",
    "policyNumber",
    "policyStartDate",
    "policyEndDate",
    "totalMembers",
    "basePremium",
    "totalFees",
    "totalDueAmount",
    "discountAmount",
    "amountPaid",
    "promotionName",
    "paymentId",
    "paymentToken",
    "source",
    "referrerName",
    "insurer",
    "action",
  ];

  get displayedColumns(): string[] {
    return this.isIssuanceReport
      ? [...this.baseColumns, "policyHolders", "memberId"]
      : this.baseColumns;
  }

  selection = new SelectionModel<any>(true, []);
  status: string = "all";
  search: string = "";
  issueDateFrom?: string;
  issueDateTo?: string;
  forAgency: boolean = false;
  @Input() isIssuanceReport: boolean = false;

  constructor(
    requestService: CareplusOrdersService,
    private _reportService: ReportService,
    private router: Router,
    private helper: Helpers,
    private snackBar: MatSnackBar,
  ) {
    super(requestService);
  }

  openPurchaseDetail(policy: CareplusPolicy): void {
    if (
      policy.transactionStatus == CareplusPolicyStatus.PAYMENT_DONE ||
      policy.transactionStatus == CareplusPolicyStatus.POLICY_ISSUED
    ) {
      this.router.navigate(["/careplus-orders/" + policy.id], {
        state: {
          policy: policy,
        },
      });
    }
  }

  onSubmit(): void {
    this.getCount().subscribe((res) => {
      this.count = res.count;
      this.loadRequests();
    });
  }

  getCount() {
    const { endDate } = this.getDateRange();
    const products = this.helper.getProducts();
    const hasCareplus = products.some(
      (product) => product.toLowerCase() === "careplus",
    );

    const basePayload = {
      filter: this.search ?? "",
      statuses: this.status === "all" || !this.status ? [] : [this.status],
      startDate: this.issueDateFrom
        ? new Date(this.issueDateFrom).toISOString()
        : undefined,
      endDate,
    };

    if (hasCareplus) {
      return this.countService.getCount({
        ...basePayload,
        referralId: this.helper.getId(),
      });
    }

    return this.countService.getCount(basePayload);
  }

  getData() {
    const { endDate } = this.getDateRange();

    let pageSize = this.paginator.pageSize ? this.paginator.pageSize : 10;
    const products = this.helper.getProducts();
    const hasCareplus = products.some(
      (product) => product.toLowerCase() === "careplus",
    );

    const basePayload = {
      skip: this.paginator.pageIndex * pageSize,
      take: pageSize,
      filter: this.search ?? "",
      statuses: this.status === "all" || !this.status ? [] : [this.status],
      startDate: this.issueDateFrom
        ? new Date(this.issueDateFrom).toISOString()
        : undefined,
      endDate,
      orderBy: this.sort.active,
      orderByDescending: this.sort.direction === "desc",
      ...(this.isIssuanceReport && { issuanceReport: true }),
    };

    if (hasCareplus) {
      return {
        ...basePayload,
        referralId: this.helper.getId(),
      };
    }

    return basePayload;
  }

  downloadReport(isXLS: boolean) {
    const { endDate } = this.getDateRange();

    const products = this.helper.getProducts();
    const hasCareplus = products.some(
      (product) => product.toLowerCase() === "careplus",
    );

    const data = {
      filter: this.search ?? "",
      statuses: this.status === "all" || !this.status ? [] : [this.status],
      startDate: this.issueDateFrom
        ? new Date(this.issueDateFrom).toISOString()
        : undefined,
      endDate,
      asXLS: isXLS,
      ...(this.isIssuanceReport && { issuanceReport: true }),
      ...(hasCareplus && { referralId: this.helper.getId() }),
    };
    this._reportService.downloadReport("DownloadCareplusOrdersReport", data);
  }

  resendEmailsForAgencyUser(id: number) {
    this.resendingId = id;

    this._careplusrequestService.resendEmail(id).subscribe({
      next: () => {
        this.snackBar.open("Policy sent successfully", "Close", {
          duration: 3000,
          horizontalPosition: "right",
          verticalPosition: "top",
          panelClass: ["success-snackbar"],
        });
        this.resendingId = null;
      },
      error: () => {
        this.snackBar.open("Failed to send policy", "Close", {
          duration: 3000,
          horizontalPosition: "right",
          verticalPosition: "top",
          panelClass: ["error-snackbar"],
        });
        this.resendingId = null;
      },
    });
  }

  private getDateRange() {
    let endDate;

    if (this.issueDateTo) {
      const end = new Date(this.issueDateTo);

      endDate = new Date(
        Date.UTC(
          end.getFullYear(),
          end.getMonth(),
          end.getDate(),
          23,
          59,
          59,
          999,
        ),
      ).toISOString();
    }

    return { endDate };
  }

  clearFilters(): void {
    this.search = "";
    this.issueDateFrom = undefined;
    this.issueDateTo = undefined;
    this.onSubmit();
  }
}
